'use client';

import React, {
  Suspense,
  createContext,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { generalConfig } from '@sola/config/lib/getters.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';
import { camelCase } from '@sola/utils/string.js';

import { getDefaultAmount, isFrequencyEnabled } from '../../../utils/fund.ts';
import {
  FundQueryParams,
  useFundQueryParams,
} from '../../../utils/query-params.tsx';
import {
  DonateAction,
  DonateActionEnum,
  DonateContextType,
  DonateFrequency,
  DonateState,
  DonateVariants,
} from './donate.types';

// Reducer
const donateReducer = (
  state: DonateState,
  action: DonateAction,
): DonateState => {
  switch (action.type) {
    case DonateActionEnum.SET_FREQUENCY:
      return {
        ...state,
        donateFrequency: action.payload,
        submitError: null,
      };

    case DonateActionEnum.SET_AMOUNT:
      return {
        ...state,
        otherSelected: false,
        selectedAmounts: {
          ...state.selectedAmounts,
          [action.payload.frequency]: action.payload.amount,
        },
        submitError: null,
      };

    case DonateActionEnum.SET_OTHER_AMOUNT:
      return {
        ...state,
        otherAmounts: {
          ...state.otherAmounts,
          [action.payload.frequency]: action.payload.amount,
        },
        otherSelected: true,
        submitError: null,
        selectedAmounts: {
          ...state.selectedAmounts,
          [action.payload.frequency]: 'other',
        },
      };

    case DonateActionEnum.SET_OTHER_SELECTED:
      return {
        ...state,
        otherSelected: action.payload,
        submitError: null,
      };

    case DonateActionEnum.SET_SUBMITTING:
      return { ...state, isSubmitting: action.payload };

    case DonateActionEnum.SET_SUBMIT_ERROR:
      return { ...state, submitError: action.payload };

    default:
      return state;
  }
};

// Context
const DonateContext = createContext<DonateContextType | undefined>(undefined);

type DonateProviderProps = FlexibleContentProps & {
  variant: DonateVariants;
  alt?: boolean;
  fundParams?: FundQueryParams;
  children: React.ReactNode;
};

// Provider
const InternalDonateProvider: React.FC<DonateProviderProps> = ({
  variant = DonateVariants.default,
  alt = false,
  fundParams,
  children,
  ...props
}) => {
  const refs = {
    [DonateFrequency.OneTime]: useRef<HTMLInputElement>(null!),
    [DonateFrequency.Monthly]: useRef<HTMLInputElement>(null!),
    [DonateFrequency.Annually]: useRef<HTMLInputElement>(null!),
  };

  const enabledFrequencies = Object.values(DonateFrequency).filter((freq) =>
    isFrequencyEnabled(freq, props.fund, fundParams),
  );

  const defaultFrequency =
    enabledFrequencies.find(
      (f) =>
        f ===
        (fundParams?.defaultFrequency ?? props?.fund?.suggestedDonateFrequency),
    ) ??
    enabledFrequencies[0] ??
    DonateFrequency.OneTime;

  const config = {
    donationLimits: {
      min: props?.fund?.minOneTimeAmount || generalConfig.donation.amountMin,
      max: props?.fund?.donationLimitTotal || generalConfig.donation.amountMax,
    },
    enabledFrequencies: enabledFrequencies as DonateFrequency[],
    defaultFrequency,
  };

  const defaultOptions =
    (fundParams?.[
      camelCase(config.defaultFrequency) as keyof FundQueryParams
    ] as number[]) ??
    props.fund?.[`suggestedAmounts${config.defaultFrequency}`];

  const getDefaultAmounts = (params?: FundQueryParams) => {
    return Object.fromEntries(
      [
        DonateFrequency.OneTime,
        DonateFrequency.Monthly,
        DonateFrequency.Annually,
      ].map((frequency) => [
        frequency,
        getDefaultAmount(frequency, props.fund, params),
      ]),
    ) as Record<DonateFrequency, string>;
  };

  const stateVariants = {
    default: {
      selectedAmounts: getDefaultAmounts(fundParams),
      suggestedAmounts: getDefaultAmounts(fundParams),
      otherAmounts: getDefaultAmounts(fundParams),
      otherSelected: !defaultOptions || defaultOptions.length < 2,
    },
    alt: {
      selectedAmounts: {
        [DonateFrequency.OneTime]: 'other',
        [DonateFrequency.Monthly]: 'other',
        [DonateFrequency.Annually]: 'other',
      },
      otherAmounts: getDefaultAmounts(),
      otherSelected: true,
    },
  };

  const initialState: DonateState = {
    donateFrequency: config.defaultFrequency,
    ...stateVariants[alt ? 'alt' : 'default'],
    isSubmitting: false,
    submitError: null,
  };
  const [state, dispatch] = useReducer(donateReducer, initialState);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      refs,
      props,
      variant,
      fundParams,
      config,
    }),
    [state, dispatch, props, variant, fundParams, config],
  );

  return (
    <DonateContext.Provider value={contextValue}>
      {children}
    </DonateContext.Provider>
  );
};

export const DonateProviderParams: React.FC<DonateProviderProps> = ({
  children,
  ...props
}) => {
  const { fundParams } = useFundQueryParams();
  return (
    <InternalDonateProvider {...props} fundParams={fundParams}>
      {children}
    </InternalDonateProvider>
  );
};

export const DonateProvider: React.FC<DonateProviderProps> = ({
  children,
  ...props
}) => {
  return (
    <Suspense
      fallback={
        <InternalDonateProvider {...props}>{children}</InternalDonateProvider>
      }
    >
      <DonateProviderParams {...props}>{children}</DonateProviderParams>
    </Suspense>
  );
};

// Hook
export const useDonate = () => {
  const context = useContext(DonateContext);
  if (context === undefined) {
    throw new Error('useDonate must be used within a DonateProvider');
  }
  return context;
};
